import { useState } from "react";

import { PageLayout } from "../../components/PageLayout";
import { BugTicketStatusBadge, BUG_TICKET_STATUS_LABELS } from "../../components/BugTicketStatusBadge";
import { BugTicketTimeline } from "../../components/BugTicketTimeline";
import { Badge, Button, CheckboxField, DropdownField, EmptyState, InputField, Text, View } from "../../components/base";
import { Icon } from "../../components/base/Icon";
import { IconButton } from "../../components/base/IconButton";
import { AdminColumn, AdminDetailDrawer, AdminStatTile, AdminTable, Field, Section, useAdminFetch } from "../../components/admin";
import { BugTicket, BugTicketComment, BugTicketStatus } from "../../redux/reducers/bugTicketSlice";
import { makeRequest } from "../../utils/request";
import { formatDateShort, formatRelative } from "../../utils/adminFormat";

const PAGE_SIZE = 50;

type TicketListResponse = {
  data: BugTicket[];
  metadata: { page: number; limit: number; total: number };
};

type CommentListResponse = {
  data: BugTicketComment[];
  metadata: { page: number; limit: number; total: number };
};

const STATUS_OPTIONS = (Object.keys(BUG_TICKET_STATUS_LABELS) as BugTicketStatus[]).map((status) => ({
  label: BUG_TICKET_STATUS_LABELS[status],
  value: status,
}));

const columns: AdminColumn<BugTicket>[] = [
  {
    key: "title",
    header: "Title",
    width: "1.6fr",
    render: (ticket) => (
      <View className="min-w-0">
        <Text className="block truncate font-medium text-gray-900 dark:text-gray-100">{ticket.title}</Text>
        <Text className="block truncate text-xs text-gray-400 dark:text-gray-500">{ticket.description}</Text>
      </View>
    ),
  },
  {
    key: "submitter",
    header: "Submitter",
    width: "1.2fr",
    render: (ticket) => <Text className="block truncate text-gray-600 dark:text-gray-300">{ticket.userEmail || ticket.userId}</Text>,
  },
  {
    key: "product",
    header: "Product",
    width: "100px",
    render: (ticket) => <Badge variant={ticket.product === "voicescore" ? "info" : "default"}>{ticket.product ?? "voicerun"}</Badge>,
  },
  {
    key: "source",
    header: "Source",
    width: "80px",
    render: (ticket) => <Badge variant="default">{ticket.source}</Badge>,
  },
  {
    key: "status",
    header: "Status",
    width: "110px",
    render: (ticket) => <BugTicketStatusBadge status={ticket.status} />,
  },
  {
    key: "updatedAt",
    header: "Updated",
    width: "110px",
    align: "right",
    sortValue: (ticket) => String(ticket.updatedAt),
    render: (ticket) => <Text className="text-gray-500 dark:text-gray-400">{formatRelative(String(ticket.updatedAt))}</Text>,
  },
  {
    key: "createdAt",
    header: "Created",
    width: "110px",
    align: "right",
    sortValue: (ticket) => String(ticket.createdAt),
    render: (ticket) => <Text className="text-gray-500 dark:text-gray-400">{formatDateShort(String(ticket.createdAt))}</Text>,
  },
];

export const AdminBugTicketsPage = () => {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [sourceFilter, setSourceFilter] = useState<string>("");
  const [productFilter, setProductFilter] = useState<string>("");
  const [selected, setSelected] = useState<BugTicket | null>(null);

  const [reply, setReply] = useState("");
  const [isInternal, setIsInternal] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [drawerError, setDrawerError] = useState<string | null>(null);

  const listUrl =
    `/v1/bug-tickets?page=${page}&limit=${PAGE_SIZE}` +
    `${statusFilter ? `&status=${statusFilter}` : ""}` +
    `${sourceFilter ? `&source=${sourceFilter}` : ""}` +
    `${productFilter ? `&product=${productFilter}` : ""}`;
  const { data, isLoading, error, refresh } = useAdminFetch<TicketListResponse>(listUrl);

  const { data: openCount, refresh: refreshOpen } = useAdminFetch<TicketListResponse>("/v1/bug-tickets?status=open&limit=1");
  const { data: inProgressCount, refresh: refreshInProgress } = useAdminFetch<TicketListResponse>("/v1/bug-tickets?status=in_progress&limit=1");

  const commentsUrl = selected ? `/v1/bug-tickets/${selected.id}/comments?limit=500` : null;
  const { data: commentsData, refresh: refreshComments } = useAdminFetch<CommentListResponse>(commentsUrl);
  const comments = commentsData?.data ?? [];

  const tickets = data?.data ?? [];
  const total = data?.metadata.total ?? 0;

  const refreshAll = () => {
    refresh();
    refreshOpen();
    refreshInProgress();
  };

  const handleSelect = (ticket: BugTicket | null) => {
    setSelected(ticket);
    setReply("");
    setIsInternal(false);
    setDrawerError(null);
  };

  const handleStatusChange = async (status: string) => {
    if (!selected || status === selected.status) return;
    setDrawerError(null);
    try {
      const { data: updated } = await makeRequest<{ data: BugTicket }>(`/v1/bug-tickets/${selected.id}`, {
        method: "PATCH",
        body: { status },
      });
      setSelected({ ...selected, ...updated });
      refreshAll();
      refreshComments();
    } catch (e) {
      setDrawerError(e instanceof Error ? e.message : "Failed to update status");
    }
  };

  const handleSendReply = async () => {
    if (!selected || !reply.trim()) return;
    setIsSending(true);
    setDrawerError(null);
    try {
      await makeRequest(`/v1/bug-tickets/${selected.id}/comments`, {
        method: "POST",
        body: { body: reply.trim(), isInternal },
      });
      setReply("");
      setIsInternal(false);
      refreshComments();
    } catch (e) {
      setDrawerError(e instanceof Error ? e.message : "Failed to send reply");
    }
    setIsSending(false);
  };

  return (
    <PageLayout
      title="Bug Tickets"
      description="User-reported bugs across the platform. Reply to users and update ticket status."
      headerAction={
        <IconButton
          icon="refresh"
          variant="info"
          onClick={refreshAll}
          title="Refresh"
        />
      }
    >
      <View className="mt-6 flex flex-col gap-4">
        <View className="grid grid-cols-2 gap-3 sm:grid-cols-3 max-w-xl">
          <AdminStatTile
            icon="bug_report"
            iconColorClass="text-blue-400"
            label="Open"
            value={openCount?.metadata.total ?? "—"}
            onClick={() => {
              setStatusFilter("open");
              setPage(1);
            }}
          />
          <AdminStatTile
            icon="clock"
            iconColorClass="text-amber-400"
            label="In Progress"
            value={inProgressCount?.metadata.total ?? "—"}
            onClick={() => {
              setStatusFilter("in_progress");
              setPage(1);
            }}
          />
          <AdminStatTile
            icon="checklist"
            iconColorClass="text-gray-400"
            label="All Tickets"
            value={statusFilter || sourceFilter || productFilter ? "…" : total}
            onClick={() => {
              setStatusFilter("");
              setSourceFilter("");
              setProductFilter("");
              setPage(1);
            }}
          />
        </View>

        <View className="flex flex-row gap-3 max-w-xl">
          <View className="flex-1">
            <DropdownField
              label="Status"
              value={statusFilter}
              onChange={(value: string) => {
                setStatusFilter(value);
                setPage(1);
              }}
              options={[{ label: "All statuses", value: "" }, ...STATUS_OPTIONS]}
            />
          </View>
          <View className="flex-1">
            <DropdownField
              label="Source"
              value={sourceFilter}
              onChange={(value: string) => {
                setSourceFilter(value);
                setPage(1);
              }}
              options={[
                { label: "All sources", value: "" },
                { label: "Web", value: "web" },
                { label: "CLI", value: "cli" },
              ]}
            />
          </View>
          <View className="flex-1">
            <DropdownField
              label="Product"
              value={productFilter}
              onChange={(value: string) => {
                setProductFilter(value);
                setPage(1);
              }}
              options={[
                { label: "All products", value: "" },
                { label: "VoiceRun", value: "voicerun" },
                { label: "VoiceScore", value: "voicescore" },
              ]}
            />
          </View>
        </View>

        {error ? (
          <EmptyState
            icon="warning-amber"
            title="Failed to load bug tickets"
            description={error}
          />
        ) : (
          <AdminTable
            columns={columns}
            rows={tickets}
            rowKey={(ticket) => ticket.id}
            onRowClick={handleSelect}
            selectedKey={selected?.id ?? null}
            isLoading={isLoading}
            emptyState={{
              icon: "bug_report",
              title: statusFilter || sourceFilter || productFilter ? "No tickets match these filters" : "No bug tickets yet",
              description: statusFilter || sourceFilter || productFilter ? "Try clearing the filters." : "User bug reports will show up here.",
            }}
            defaultSort={{ key: "updatedAt", dir: "desc" }}
            pagination={{ page, pageSize: PAGE_SIZE, total, onPageChange: setPage }}
          />
        )}
      </View>

      <AdminDetailDrawer
        isOpen={selected !== null}
        onClose={() => handleSelect(null)}
        title={selected?.title ?? ""}
        subtitle={selected ? `${selected.userEmail || selected.userId} · ${formatRelative(String(selected.createdAt))}` : undefined}
        width={560}
      >
        {selected && (
          <>
            <Section title="Status">
              <DropdownField
                value={selected.status}
                onChange={handleStatusChange}
                options={STATUS_OPTIONS}
              />
              <Text className="mt-1 block text-xs text-gray-400 dark:text-gray-500">Status changes notify the submitter and are recorded on the timeline.</Text>
            </Section>

            <Section title="Report">
              <Text className="block whitespace-pre-wrap text-sm text-gray-900 dark:text-gray-100">{selected.description}</Text>
              {selected.screenshotUrl && (
                <a
                  href={selected.screenshotUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  <img
                    src={selected.screenshotUrl}
                    alt="Bug report screenshot"
                    className="mt-2 max-h-48 rounded-lg border border-gray-200 dark:border-gray-700"
                  />
                </a>
              )}
              <View className="mt-3 flex flex-col gap-2">
                <Field
                  label="Ticket ID"
                  mono
                >
                  {selected.id}
                </Field>
                <Field label="Product">
                  <Badge variant={selected.product === "voicescore" ? "info" : "default"}>{selected.product ?? "voicerun"}</Badge>
                </Field>
                <Field label="Source">
                  <Badge variant="default">{selected.source}</Badge>
                </Field>
                {selected.cliVersion && <Field label="CLI Version">{selected.cliVersion}</Field>}
                {selected.osInfo && <Field label="OS">{selected.osInfo}</Field>}
                <Field label="Created">{formatDateShort(String(selected.createdAt))}</Field>
              </View>
            </Section>

            <Section title="Conversation">
              <BugTicketTimeline
                comments={comments}
                submitterLabel={selected.userEmail || "Submitter"}
              />

              <View className="mt-3 flex flex-col gap-2">
                {drawerError && <Text className="text-sm text-red-600 dark:text-red-400">{drawerError}</Text>}
                <InputField
                  type="textarea"
                  value={reply}
                  onChange={setReply}
                  placeholder={isInternal ? "Write an internal note (not visible to the user)…" : "Reply to the user…"}
                  isDisabled={isSending}
                />
                <View className="flex flex-row items-center justify-between">
                  <CheckboxField
                    label="Internal note"
                    value={isInternal}
                    onChange={setIsInternal}
                  />
                  <Button
                    variant={isInternal ? "warning" : "success"}
                    size="sm"
                    outline
                    onClick={handleSendReply}
                    isDisabled={isSending || !reply.trim()}
                  >
                    {isSending ? (
                      <Icon
                        name="loader"
                        size={14}
                        className="animate-spin"
                      />
                    ) : (
                      <Icon
                        name="send"
                        size={14}
                      />
                    )}
                    {isInternal ? "Add Note" : "Send Reply"}
                  </Button>
                </View>
              </View>
            </Section>
          </>
        )}
      </AdminDetailDrawer>
    </PageLayout>
  );
};
